<template>
  <div class="payment-page">
    <StepProgress />
    
    <v-container class="py-8">
      <v-row justify="center">
        <v-col cols="12" md="8" lg="6">
          <v-card class="main-card" elevation="0">
            <v-card-title class="text-h4 text-center pa-6 primary--text">
              Payment
            </v-card-title>
            
            <v-card-text class="pa-6">
              <!-- Service Cost Information -->
              <div class="service-info mb-8">
                <h3 class="text-h5 mb-4 d-flex align-center">
                  <v-icon left size="28" color="primary" class="mr-2">mdi-heart</v-icon>
                  Digital Memorial Service
                </h3>

                <div class="mb-6">
                  <div class="price-display mb-4">
                    <span v-if="introOfferActive" class="price-amount original-price">{{ formattedPrice }}</span>
                    <span v-if="introOfferActive" class="intro-price">Free</span>
                    <span v-else class="price-amount">{{ formattedPrice }}</span>
                    <span class="price-period">{{ introOfferActive ? 'introductory offer' : 'one-time' }}</span>
                  </div>
                </div>

                <div class="service-features">
                  <v-list density="compact" class="service-list">
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon color="success">mdi-check</v-icon>
                      </template>
                      <v-list-item-title>Add photos and stories</v-list-item-title>
                    </v-list-item>
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon color="success">mdi-check</v-icon>
                      </template>
                      <v-list-item-title>Share with family and friends</v-list-item-title>
                    </v-list-item>
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon color="success">mdi-check</v-icon>
                      </template>
                      <v-list-item-title>High-quality printable version</v-list-item-title>
                    </v-list-item>
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon color="success">mdi-check</v-icon>
                      </template>
                      <v-list-item-title>Secure hosting</v-list-item-title>
                    </v-list-item>
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon color="success">mdi-check</v-icon>
                      </template>
                      <v-list-item-title>No monthly fees</v-list-item-title>
                    </v-list-item>
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon color="success">mdi-check</v-icon>
                      </template>
                      <v-list-item-title>Slideshow & Export to PDF</v-list-item-title>
                    </v-list-item>
                    <v-list-item>
                      <template v-slot:prepend>
                        <v-icon color="success">mdi-check</v-icon>
                      </template>
                      <v-list-item-title>One year data retention</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </div>
              </div>

              <!-- Promotion Section - Only show when payment not completed -->
              <div v-if="!paymentCompleted" class="promotion-section mb-8">
                <h3 class="text-h5 mb-4">Promotion</h3>

                <p class="text-body-1 mb-4">
                  Your free introductory offer is ready. The promo code is fixed and will be applied automatically.
                  No credit card or payment details are required.
                </p>

                <div class="d-flex align-center promo-input-row">
                  <v-text-field
                    v-model="promoCode"
                    label="Promo Code"
                    variant="outlined"
                    density="comfortable"
                    readonly
                    class="promo-code-input"
                    disabled
                  ></v-text-field>

                  <v-btn
                    v-if="!promoApplied"
                    color="primary"
                    @click="validatePromoCode"
                    :loading="promoValidating"
                    :disabled="promoValidating"
                    size="large"
                  >
                    Apply Offer
                  </v-btn>
                </div>

                <v-alert
                  v-if="promoMessage"
                  :type="promoApplied ? 'success' : 'warning'"
                  variant="tonal"
                  class="mt-4"
                >
                  {{ promoMessage }}
                </v-alert>
              </div>

              <!-- Payment Form -->
              <div v-if="!introOfferActive && !paymentCompleted && !promoIsFree" class="payment-form">
                <h3 class="text-h5 mb-4">Payment Information</h3>
                
                <v-form ref="paymentForm" v-model="paymentValid">
                  <v-text-field
                    v-model="cardholderName"
                    label="Cardholder Name"
                    variant="outlined"
                    density="comfortable"
                    :rules="nameRules"
                    class="mb-4"
                    placeholder="John Doe"
                    autocomplete="cc-name"
                  ></v-text-field>

                  <div class="card-element-section mb-4">
                    <label class="card-element-label">Card Details</label>
                    <div id="card-element" class="stripe-card-element"></div>
                    <p v-if="cardError" class="text-error text-body-2 mt-2">{{ cardError }}</p>
                  </div>

                  <v-checkbox
                    v-model="confirmTerms"
                    :rules="[v => v || 'Please confirm the payment authorisation']"
                    label="I authorise Passing On to charge my card for this one-time payment."
                    class="mt-2"
                  ></v-checkbox>

                  <v-alert v-if="paymentError" type="error" variant="tonal" class="mt-4">
                    {{ paymentError }}
                  </v-alert>

                  <v-alert v-if="initializingPayment" type="info" variant="tonal" class="mt-4">
                    Initialising secure payment form...
                  </v-alert>
                </v-form>

                <v-alert type="info" variant="tonal" class="mt-6 mb-4">
                  Your payment information is encrypted and secure. We use industry-standard SSL encryption to protect your data.
                </v-alert>
              </div>

              <!-- Payment Success -->
              <v-expand-transition>
                <div v-if="paymentCompleted && !promoIsFree" class="payment-success">
                  <v-card class="success-card" elevation="0">
                    <v-card-text class="text-center pa-6">
                      <v-icon size="64" color="success" class="mb-4">
                        mdi-check-circle
                      </v-icon>
                      <h3 class="text-h5 mb-4">Payment Successful!</h3>
                      <p class="text-body-1 text--secondary mb-4">
                        Your payment of {{ formattedPrice }} has been processed successfully.
                      </p>
                      <p class="text-body-2 text--secondary">
                        Your memorial for {{ store.personName }} is now active and ready to be shared with family and friends.
                      </p>

                      <div class="payment-details mt-4">
                        <v-divider class="mb-4"></v-divider>
                        <div class="text-left">
                          <p v-if="transactionId"><strong>Transaction ID:</strong> {{ transactionId }}</p>
                          <p v-if="paymentDate"><strong>Date:</strong> {{ paymentDate }}</p>
                          <p><strong>Amount:</strong> {{ formattedPrice }}</p>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>
              </v-expand-transition>

              <!-- Promo Code Success (Free) -->
              <v-expand-transition>
                <div v-if="paymentCompleted && promoIsFree" class="payment-success">
                  <v-card class="success-card" elevation="0">
                    <v-card-text class="text-center pa-6">
                      <v-icon size="64" color="success" class="mb-4">
                        mdi-check-circle
                      </v-icon>
                      <h3 class="text-h5 mb-4">Promo Code Applied!</h3>
                      <p class="text-body-1 text--secondary mb-4">
                        Your promo code has been applied successfully. No payment is required.
                      </p>
                      <p class="text-body-2 text--secondary">
                        Your memorial for {{ store.personName }} is now active and ready to be shared with family and friends.
                      </p>
                    </v-card-text>
                  </v-card>
                </div>
              </v-expand-transition>
            </v-card-text>

            <v-card-actions class="pa-6 pt-0">
              <v-btn
                variant="text"
                @click="previousStep"
                :disabled="processing"
                class="mr-4"
              >
                <v-icon left>mdi-arrow-left</v-icon>
                Back
              </v-btn>
              
              <v-spacer></v-spacer>
              
              <v-btn
                v-if="!introOfferActive && !paymentCompleted && !promoIsFree"
                color="primary"
                size="large"
                rounded="0"
                elevation="0"
                :disabled="!canSubmitPayment"
                :loading="processing"
                @click="processPayment"
                class="px-8"
              >
                Pay {{ formattedPrice }}
                <v-icon right class="ml-2">mdi-credit-card</v-icon>
              </v-btn>

              <v-btn
                v-if="paymentCompleted"
                color="primary"
                size="large"
                rounded="0"
                elevation="0"
                @click="nextStep"
                class="px-8"
              >
                {{ promoIsFree ? 'Next Page' : 'Continue to Share' }}
                <v-icon right class="ml-2">mdi-arrow-right</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { loadStripe } from '@stripe/stripe-js'
import StepProgress from '../components/StepProgress.vue'
import { useMemorialStore } from '../stores/memorial'
import apiService from '../services/api'

const router = useRouter()
const store = useMemorialStore()

const paymentForm = ref(null)
const paymentValid = ref(false)
const processing = ref(false)
const initializingPayment = ref(false)
const paymentCompleted = ref(false)
const transactionId = ref('')
const paymentDate = ref('')
const paymentError = ref('')
const cardError = ref('')

const cardholderName = ref('')
const nameRules = [
  v => !!v || 'Cardholder name is required',
  v => v.length >= 2 || 'Name must be at least 2 characters'
]

const confirmTerms = ref(false)

// Promo code state
const INTRO_PROMO_CODE = 'intro26'
const introOfferActive = true
const promoCode = ref(INTRO_PROMO_CODE)
const promoValidating = ref(false)
const promoApplied = ref(false)
const promoIsFree = ref(false)
const promoMessage = ref('')

const stripeInstance = ref(null)
const stripeElements = ref(null)
const cardElement = ref(null)
const cardComplete = ref(false)

const paymentIntentClientSecret = ref('')
const stripeCustomerId = ref('')
const paymentAmount = ref(3000)
const paymentCurrency = ref('aud')

const formattedPrice = computed(() => {
  const amount = paymentAmount.value
  if (amount != null) {
    try {
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: (paymentCurrency.value || 'aud').toUpperCase()
      }).format(amount / 100)
    } catch (error) {
      return `$${(amount / 100).toFixed(2)}`
    }
  }
  return '$30.00'
})

const canSubmitPayment = computed(() => {
  return paymentValid.value && cardComplete.value && confirmTerms.value && !processing.value && !initializingPayment.value && !!paymentIntentClientSecret.value
})

const mountCardElement = async () => {
  if (cardElement.value || stripeInstance.value) {
    return
  }

  const publishableKey = process.env.VUE_APP_STRIPE_PUBLIC_KEY
  if (!publishableKey) {
    paymentError.value = 'Stripe is not configured for this environment. Please contact support.'
    return
  }

  try {
    stripeInstance.value = await loadStripe(publishableKey)
    if (!stripeInstance.value) {
      paymentError.value = 'Unable to initialise payment form.'
      return
    }

    stripeElements.value = stripeInstance.value.elements()
    cardElement.value = stripeElements.value.create('card', {
      hidePostalCode: true,
      style: {
        base: {
          color: '#1f1f1f',
          fontFamily: 'Roboto, sans-serif',
          fontSize: '16px',
          '::placeholder': {
            color: 'var(--v-theme-neutral400)'
          }
        },
        invalid: {
          color: '#b00020'
        }
      }
    })

    cardElement.value.mount('#card-element')
    cardElement.value.on('change', event => {
      cardError.value = event.error ? event.error.message : ''
      cardComplete.value = event.complete
    })
  } catch (error) {
    console.error('Stripe initialisation error:', error)
    paymentError.value = 'We were unable to initialise the payment form. Please refresh the page.'
  }
}

const validatePromoCode = async () => {
  if (!promoCode.value || promoCode.value.trim() === '') {
    return
  }

  if (!store.apiToken) {
    promoMessage.value = 'Please sign in to validate promo codes.'
    return
  }

  if (!store.memorialUuid) {
    promoMessage.value = 'Memorial information is missing. Please save your memorial first.'
    return
  }

  promoValidating.value = true
  promoMessage.value = ''

  try {
    const response = await apiService.validatePromoCode(promoCode.value.trim(), store.memorialUuid, store.apiToken)

    // Log the API response
    console.log('Promo code validation response:', response)

    if (response.success) {
      promoApplied.value = true
      promoIsFree.value = response.free || false

      if (response.free) {
        promoMessage.value = 'Promo code applied successfully! No payment is required.'
        paymentCompleted.value = true
      } else {
        promoMessage.value = 'Promo code applied successfully!'

        // Update payment amount with discount
        if (response.amount) {
          // Subtract the discount amount from the current payment amount
          const discountedAmount = paymentAmount.value - response.amount
          paymentAmount.value = discountedAmount > 0 ? discountedAmount : 0
        }
      }
    } else {
      promoApplied.value = false
      // Softer message for invalid codes with limited attempts warning
      if (response.msg === 'max promo check reached') {
        promoMessage.value = response.msg
      } else {
        promoMessage.value = "We couldn't find that promo code. Please double-check and try again. Note: You have a limited number of attempts."
      }
    }
  } catch (error) {
    console.error('Promo validation error:', error)
    promoMessage.value = 'Unable to validate promo code. Please try again.'
  } finally {
    promoValidating.value = false
  }
}

const initializePaymentIntent = async () => {
  if (!store.apiToken) {
    paymentError.value = 'Your session has expired. Please sign in again before making a payment.'
    return
  }

  if (!store.memorialUuid) {
    paymentError.value = 'Please save your memorial details before processing payment.'
    return
  }

  initializingPayment.value = true
  paymentError.value = ''

  try {
    const response = await apiService.createPaymentIntent(store.memorialUuid, store.user?.uuid, store.apiToken)

    if (!response.success) {
      paymentError.value = response.msg || 'Unable to start payment. Please try again.'
      return
    }

    paymentIntentClientSecret.value = response.clientSecret
    stripeCustomerId.value = response.stripeCustomerId || null

    if (typeof response.amount !== 'undefined') {
      paymentAmount.value = response.amount
    }

    if (response.currency) {
      paymentCurrency.value = response.currency
    }
  } catch (error) {
    const errorInfo = apiService.formatErrorMessage(error)
    paymentError.value = errorInfo.message
  } finally {
    initializingPayment.value = false
  }
}

const processPayment = async () => {
  if (!paymentForm.value) {
    return
  }

  const isFormValid = await paymentForm.value.validate()
  if (!isFormValid) {
    return
  }

  if (!cardComplete.value) {
    cardError.value = 'Please enter your card details.'
    return
  }

  if (!paymentIntentClientSecret.value || !stripeInstance.value || !cardElement.value) {
    paymentError.value = 'Payment form is not ready. Please refresh the page and try again.'
    return
  }

  const userUuid = store.user?.uuid
  if (!userUuid) {
    paymentError.value = 'We were unable to verify your account. Please sign in again.'
    return
  }

  processing.value = true
  cardError.value = ''
  paymentError.value = ''

  try {
    const { error, paymentIntent } = await stripeInstance.value.confirmCardPayment(paymentIntentClientSecret.value, {
      payment_method: {
        card: cardElement.value,
        billing_details: {
          name: cardholderName.value,
          email: store.userEmail
        }
      }
    })

    if (error) {
      cardError.value = error.message || 'Payment could not be completed.'
      return
    }

    if (!paymentIntent || paymentIntent.status !== 'succeeded') {
      paymentError.value = 'Payment did not complete. Please try again.'
      return
    }

    transactionId.value = paymentIntent.id
    paymentDate.value = new Date(paymentIntent.created * 1000).toLocaleString()
    paymentIntentClientSecret.value = ''

    const payload = {
      userUuid,
      memorialUuid: store.memorialUuid,
      stripePaymentConfirmation: paymentIntent.id,
      paymentIntentId: paymentIntent.id,
      stripeCustomerId: paymentIntent.customer || stripeCustomerId.value
    }

    const result = await apiService.processPayment(payload, store.apiToken)

    if (!result.success) {
      paymentError.value = result.msg || 'Unable to finalise payment with the server.'
      return
    }

    if (result.memorial) {
      stripeCustomerId.value = result.memorial.stripeCustomerId || stripeCustomerId.value
      store.completePayment(result.memorial)
    } else {
      store.completePayment()
    }

    paymentCompleted.value = true
  } catch (error) {
    console.error('Payment error:', error)
    const errorInfo = apiService.formatErrorMessage(error)
    paymentError.value = errorInfo.message
  } finally {
    processing.value = false
  }
}

const nextStep = () => {
  store.goToStep(7) // Contacts step
  router.push('/contacts')
}

const previousStep = () => {
  store.goToStep(5) // Save step
  router.push('/save')
}

onMounted(async () => {
  // If user has a memorial UUID, reload from server to ensure fresh data
  if (store.memorialUuid && store.isAccountCreated && store.isEmailVerified) {
    console.log('Reloading memorial data from server...')
    const loaded = await store.loadUserMemorialFromServer()
    if (loaded) {
      console.log('Memorial data reloaded successfully')

      // Check if a promo was applied
      if (store.promoApplied?.code?.toLowerCase() === INTRO_PROMO_CODE) {
        promoApplied.value = true
        promoIsFree.value = store.promoApplied.free || false

        if (store.promoApplied.free) {
          promoMessage.value = 'Promo code applied successfully! No payment is required.'
          paymentCompleted.value = true
        } else {
          promoMessage.value = 'Promo code applied successfully!'
        }
      }
    }
  }

  store.goToStep(6) // Set current step to Payment

  if (!promoIsFree.value) {
    paymentCompleted.value = store.paymentCompleted
  }

  if (store.paymentCompletedAt) {
    paymentDate.value = new Date(store.paymentCompletedAt).toLocaleString()
  }

  if (store.stripeCustomerId) {
    stripeCustomerId.value = store.stripeCustomerId
  }

  if (paymentCompleted.value && !promoIsFree.value) {
    return
  }

  if (introOfferActive) {
    if (!promoApplied.value) {
      await validatePromoCode()
    }
    return
  }

  await mountCardElement()
  await initializePaymentIntent()
})

onUnmounted(() => {
  if (cardElement.value) {
    cardElement.value.unmount()
    cardElement.value = null
  }
  stripeElements.value = null
  stripeInstance.value = null
})
</script>

<style lang="scss" scoped>
@use "~@/styles/variables" as *;
@use "~@/styles/mixins" as *;
.payment-page {
  @include page-container;
}

.main-card {
  @include main-card;
  
  .v-card-title {
    @include section-header;
  }
}

.service-info {
  background: $gray-50;
  border-radius: $border-radius-lg;
  padding: 2rem;
  border: $border-width-thin solid $border;
}

.price-display {
  margin: 2rem 0;
}

.price-amount {
  font-size: 3rem;
  font-weight: bold;
  color: $primary;
}

.original-price {
  color: var(--v-theme-gray500);
  font-size: 1.5rem;
  text-decoration: line-through;
}

.intro-price {
  color: $primary;
  font-size: 3rem;
  font-weight: bold;
  margin-left: 0.75rem;
}

.price-period {
  font-size: 1.2rem;
  color: var(--v-theme-gray600);
  margin-left: 0.5rem;
}

.service-list {
  background: transparent;
  text-align: left;
}

.promotion-section {
  background: $gray-50;
  border-radius: $border-radius-lg;
  padding: 2rem;
  border: $border-width-thin solid $border;
}

.promo-input-row {
  gap: 1rem;
}

.promo-code-input {
  width: 250px;
  flex-grow: 0;
  flex-shrink: 0;
}

.payment-form {
  background: $gray-50;
  border-radius: $border-radius-lg;
  padding: 2rem;
  border: $border-width-thin solid $border;
}

.success-card {
  border: $border-width solid $success;
  background: $white;
  border-radius: $border-radius;
}

.payment-details {
  background: white;
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid var(--v-theme-border);
}

.card-element-section {
  display: flex;
  flex-direction: column;
}

.card-element-label {
  font-weight: 600;
  margin-bottom: 8px;
  color: rgba(0, 0, 0, 0.6);
}

.stripe-card-element {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  padding: 14px 12px;
  background-color: var(--v-theme-surface);
  transition: box-shadow 0.2s ease;
}

.stripe-card-element.StripeElement--focus {
  box-shadow: 0 0 0 2px rgba(79, 195, 247, 0.35);
}

@include mobile {
  .pa-6 {
    padding: 1rem !important;
  }

  .service-info {
    padding: 1rem;
  }

  .promotion-section {
    padding: 1rem;
  }

  .payment-form {
    padding: 1rem;
  }

  .price-amount {
    font-size: 2.5rem;
  }
}
</style>
